import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useAuth } from "../services/auth.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFileAlt,
  faEdit,
  faImage,
  faVideo,
  faCog,
  faList,
  faComment,
  faReply,
  faLink,
  faCheck,
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import { 
  Box, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    maxWidth: '800px',
    width: '95%',
    maxHeight: '90vh',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fafafa',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      '& fieldset': {
        borderColor: '#667eea',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#667eea',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: '#666',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 24px',
  fontSize: '14px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const FormSection = styled(Card)(({ theme }) => ({
  marginBottom: '16px',
  borderRadius: '12px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #f0f0f0',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const FormSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#2c3e50',
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '16px',
}));

const SectionIcon = styled(Avatar)(({ theme }) => ({
  width: '28px',
  height: '28px',
  backgroundColor: '#667eea',
  color: 'white',
  fontSize: '12px',
}));

const FieldContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: '16px',
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#2c3e50',
  marginBottom: '6px',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}));

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  color: '#666',
  marginTop: '4px',
  fontStyle: 'italic',
}));

export default function NewModelModal(props) {
  const [selectOptions, setSelectOptions] = useState([]);
  const [selectItem, setSelectItem] = useState(null);
  const { user, logout, currentConfig, changeCurrentConfig, getCurrentConfig } = useAuth();
  const [newdatasource, setNewDatasource] = useState(null);
  const [formItems, setFormItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    //check if props.body contains and item with type select
    let countselectdatasources = 0;
    props.body.forEach((item) => {
      if (item.type === "select-datasource") {
        setNewDatasource(item);
        setSelectItem({ type: item.type, dataSource: item.dataSource });
        countselectdatasources++;
      }
    });
    if (countselectdatasources === 0) {
      setSelectItem({ type: "select", dataSource: null });
    }
    setFormItems(props.body);
  }, [props.body]);

  useEffect(() => {
    // Check if modal should be open
    const modal = document.querySelector(".main-modal");
    if (modal && modal.style.display === 'flex') {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, []);

  const selectShow = (e, action, items) => {
    props.onChange(e, action);
    document.getElementById(e.value).classList.remove("hidden");
    let inputclasses = "form-control block w-full px-3 py-1.5 0text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none".split(' ');
    document.getElementById(e.value).classList.add(...inputclasses);
    items.forEach((item) => {
      if (item !== e.value) {
        document.getElementById(item).classList.add("hidden");
        document.getElementById(item).value = "";
      }
    }
    );
  }

  const selectSource = (e, action, items) => {
    const value = e.value;
    let newFormItems = formItems.map((selecteditem, index) => {

      if (selecteditem.type !== "select") return selecteditem;
      if (!newdatasource.nextSourceIds.includes(selecteditem.id)) {
        return selecteditem;
      }
      else {
        selecteditem.dataSource = value;
        return selecteditem;
      }

    });
    setFormItems(newFormItems);
    props.onChange(e, action);
  }

  useEffect(() => {
    //fetch data from selectItem.url
    if (selectItem) {
      async function fetchData() {
        let allOptions = [];
        await Promise.all(formItems.map(async (selecteditem, index) => {
          if (selecteditem.type === "select-add")
            allOptions.push({ id: index, options: [] });
          if (selecteditem.type !== "select" && selecteditem.type !== "select-datasource")
            return;
          let response = await fetch(process.env.REACT_APP_BACKEND_API + selecteditem.dataSource, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": user.accessToken,
            },
          })
            .then((response) => {
              if (response.ok) {
                return response.json();
              } else {
                throw new Error("Something went wrong ...");
              }
            })
            .then((data) => {
              let options = data.map((item) => {
                return { value: item[selecteditem.displayColumn ? selecteditem.displayColumn : "_id"], label: item[selecteditem.displayColumn ? selecteditem.displayColumn : "_id"], disabled: item.ejecutivos > 0 ? false : true };
              });
              let newSelectOptions = { id: index, options: options };
              return newSelectOptions;
            })
            .catch((error) => {
              return null;
            });
          if (response) {
            allOptions.push(response);
          }
        }));
        setSelectOptions(allOptions.filter(Boolean));
      }
      fetchData();
    }

  }, [selectItem, formItems]);

  const options = [
    { value: "chocolate", label: "Chocolate" },
    { value: "strawberry", label: "Strawberry" },
    { value: "vanilla", label: "Vanilla" },
  ];

  const handleClose = () => {
    setOpen(false);
    props.onClose();
  };

  // Group form items by sections
  const groupFormItems = () => {
    const groups = {
      basic: [],
      content: [],
      parameters: [],
      media: [],
      buttons: [],
      advanced: []
    };

    formItems.forEach((item) => {
      if (item.name === 'name' || item.name === 'body') {
        groups.basic.push(item);
      } else if (item.name === 'source' || item.name === 'template_origin') {
        groups.content.push(item);
      } else if (item.name.includes('parameter') && !item.name.includes('button')) {
        groups.parameters.push(item);
      } else if (item.name === 'image' || item.name === 'video' || item.name === 'image-video-select') {
        groups.media.push(item);
      } else if (item.name.includes('button')) {
        groups.buttons.push(item);
      } else {
        groups.advanced.push(item);
      }
    });

    return groups;
  };

  const getFieldIcon = (fieldName) => {
    switch (fieldName) {
      case 'name':
        return <FontAwesomeIcon icon={faFileAlt} />;
      case 'body':
        return <FontAwesomeIcon icon={faComment} />;
      case 'image':
        return <FontAwesomeIcon icon={faImage} />;
      case 'video':
        return <FontAwesomeIcon icon={faVideo} />;
      case 'source':
        return <FontAwesomeIcon icon={faLink} />;
      default:
        return <FontAwesomeIcon icon={faCog} />;
    }
  };

  const getSectionIcon = (sectionName) => {
    switch (sectionName) {
      case 'Información Básica':
        return <FontAwesomeIcon icon={faFileAlt} />;
      case 'Contenido':
        return <FontAwesomeIcon icon={faComment} />;
      case 'Multimedia':
        return <FontAwesomeIcon icon={faImage} />;
      case 'Parámetros':
        return <FontAwesomeIcon icon={faList} />;
      case 'Botones':
        return <FontAwesomeIcon icon={faReply} />;
      case 'Configuración Avanzada':
        return <FontAwesomeIcon icon={faCog} />;
      default:
        return <FontAwesomeIcon icon={faCog} />;
    }
  };

  const renderField = (item, key) => {
    const fieldIcon = getFieldIcon(item.name);
    
    switch (item.type) {
      case "text":
      case "email":
      case "password":
      case "number":
        return (
          <StyledTextField
            key={key}
            fullWidth
            type={item.type}
            label={item.placeholder}
            value={item.value || ""}
            onChange={props.onChange}
            name={item.name}
            variant="outlined"
            size="medium"
            sx={{ mb: 2 }}
          />
        );
      
      case "hidden":
        return (
          <input
            key={key}
            id={item.id}
            name={item.name}
            type="hidden"
            value={item.value || ""}
            onChange={props.onChange}
          />
        );
      
      case "checkbox":
        return (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={item.value === "on" || item.value === true}
                onChange={props.onChange}
                name={item.name}
                color="primary"
                sx={{
                  '&.Mui-checked': {
                    color: '#667eea',
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {fieldIcon}
                {item.placeholder}
              </Box>
            }
            sx={{ mb: 2 }}
          />
        );
      
      case "textarea":
        return (
          <Box key={key}>
            <StyledTextField
              fullWidth
              multiline
              rows={6}
              label={item.placeholder}
              value={item.value || ""}
              onChange={props.onChange}
              name={item.name}
              variant="outlined"
              size="medium"
              sx={{ mb: 1 }}
            />
            <HelperText>
              💡 Usa {'{1}'}, {'{2}'}, etc. para parámetros dinámicos. Puedes usar *texto* para negrita y _texto_ para cursiva.
            </HelperText>
          </Box>
        );
      
      case "select":
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
              {item.placeholder}
            </Typography>
            <Select
              isMulti={item.multiselect}
              placeholder={item.placeholder}
              value={item.value || ""}
              name={item.name}
              onChange={props.onChange}
              isLoading={selectOptions ? false : true}
              options={selectOptions ? selectOptions.find((selectitem) => selectitem && selectitem.id === key)?.options : options}
              isOptionDisabled={() => item.value && item.value.length > item.limit}
              defaultValue={item.defaultValue}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  minHeight: '48px',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: '#f5f5f5',
                  },
                  '&.select__control--is-focused': {
                    borderColor: '#667eea',
                    borderWidth: '2px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)',
                  },
                }),
                menu: (provided) => ({
                  ...provided,
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  border: '1px solid #f0f0f0',
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#f0f0f0' : 'white',
                  color: state.isSelected ? 'white' : '#333',
                  padding: '10px 16px',
                  '&:hover': {
                    backgroundColor: state.isSelected ? '#667eea' : '#f0f0f0',
                  },
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: '#667eea',
                  color: 'white',
                  borderRadius: '8px',
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: 'white',
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }),
              }}
            />
            {item.limit && (
              <HelperText>
                Máximo {item.limit} elementos seleccionables
              </HelperText>
            )}
          </Box>
        );
      
      case "select-add":
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
              {item.placeholder}
            </Typography>
            <Select
              isMulti={item.multiselect}
              placeholder={item.placeholder}
              value={item.value || ""}
              name={item.name}
              onChange={props.onChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  let newOption = { value: e.target.value, label: e.target.value };
                  let newOptions = selectOptions.find((selectitem) => selectitem && selectitem.id === key)?.options;
                  if (!newOptions) newOptions = [];
                  newOptions.push(newOption);
                  let newSelectOptions = selectOptions.map((selectitem) => {
                    if (selectitem && selectitem.id === key) {
                      selectitem.options = newOptions;
                    }
                    return selectitem;
                  });
                  setSelectOptions(newSelectOptions);
                }
              }}
              isLoading={selectOptions ? false : true}
              options={selectOptions ? selectOptions.find((selectitem) => selectitem && selectitem.id === key)?.options : options}
              isOptionDisabled={() => item.value && item.value.length > item.limit}
              defaultValue={item.defaultValue}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  minHeight: '48px',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: '#f5f5f5',
                  },
                  '&.select__control--is-focused': {
                    borderColor: '#667eea',
                    borderWidth: '2px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)',
                  },
                }),
              }}
            />
          </Box>
        );
      
      case "select-fixed":
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
              {item.placeholder}
            </Typography>
            <Select
              isMulti={item.multiselect}
              placeholder={item.placeholder}
              value={item.value || ""}
              name={item.name}
              onChange={props.onChange}
              isLoading={selectOptions ? false : true}
              options={item.options.map((option) => { return { value: option, label: option } })}
              isOptionDisabled={() => item.value && item.value.length > item.limit}
              defaultValue={item.defaultValue}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  minHeight: '48px',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: '#f5f5f5',
                  },
                  '&.select__control--is-focused': {
                    borderColor: '#667eea',
                    borderWidth: '2px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)',
                  },
                }),
              }}
            />
          </Box>
        );
      
      case "select-show":
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
              {item.placeholder}
            </Typography>
            <Select
              placeholder={item.placeholder}
              value={item.value || ""}
              name={item.name}
              onChange={(e, action) => selectShow(e, action, item.optionIds)}
              options={item.optionIds.map((optionId) => { return { value: optionId, label: optionId } })}
              defaultValue={item.defaultValue}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  minHeight: '48px',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: '#f5f5f5',
                  },
                  '&.select__control--is-focused': {
                    borderColor: '#667eea',
                    borderWidth: '2px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)',
                  },
                }),
              }}
            />
          </Box>
        );
      
      case "select-datasource":
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
              {item.placeholder}
            </Typography>
            <Select
              placeholder={item.placeholder}
              value={item.value || ""}
              name={item.name}
              onChange={(e, action) => selectSource(e, action, item.nextSourceIds)}
              options={selectOptions ? selectOptions.find((selectitem) => selectitem && selectitem.id === key)?.options : options}
              defaultValue={item.defaultValue}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  minHeight: '48px',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: '#f5f5f5',
                  },
                  '&.select__control--is-focused': {
                    borderColor: '#667eea',
                    borderWidth: '2px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)',
                  },
                }),
              }}
            />
          </Box>
        );
      
      default:
        return null;
    }
  };

  const renderFormSection = (title, items, icon = null) => {
    if (items.length === 0) return null;
    
    const sectionIcon = getSectionIcon(title);
    
    return (
      <FormSection>
        <CardContent sx={{ p: 2 }}>
          <FormSectionTitle variant="h6">
            <SectionIcon>
              {sectionIcon}
            </SectionIcon>
            {title}
          </FormSectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {items.map((item, key) => (
              <Box key={key}>
                {renderField(item, key)}
              </Box>
            ))}
          </Box>
        </CardContent>
      </FormSection>
    );
  };

  const groups = groupFormItems();

  return (
    <>
      {/* Legacy modal for backward compatibility */}
      <div className="main-modal fixed w-full h-100 inset-0 z-50 overflow-hidden flex justify-center items-center animated fadeIn faster backdrop-blur-sm bg-black/30" style={{ display: 'none' }}>
        <div className="border border-gray-200 rounded-lg shadow-lg modal-container bg-white w-11/12 md:w-fit mx-auto rounded shadow-lg z-50 overflow-y-auto h-5/6">
          <div className="modal-content py-4 text-left px-6">
            <div className="flex justify-between items-center pb-3">
              <p className="text-2xl font-bold">{props.title}</p>
              <div
                className="modal-close cursor-pointer z-50"
                onClick={props.onClose}
              >
                <svg
                  className="fill-current text-black"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                >
                  <path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"></path>
                </svg>
              </div>
            </div>
            <div className="my-5">
              <div class="block p-6 rounded-lg shadow-lg bg-white max-w-md">
                <form
                  action={props.action}
                  onSubmit={props.onSubmit}
                  method={props.method}
                >
                  <div class="grid ">
                    {formItems.map((item, key) => {
                      if (
                        item.type === "text" ||
                        item.type === "email" ||
                        item.type === "password" ||
                        item.type === "number"
                      )
                        return (
                          <div class="form-group m-3">
                            <input
                              type={item.type}
                              onChange={props.onChange}
                              value={item.value}
                              class="form-control
                              block
                              w-full
                              px-3
                              py-1.5
                              text-base
                              font-normal
                              text-gray-700
                              bg-white bg-clip-padding
                              border border-solid border-gray-300
                              rounded
                              transition
                              ease-in-out
                              m-0
                              focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
                              id={item.id}
                              key={key}
                              name={item.name}
                              aria-describedby="emailHelp123"
                              placeholder={item.placeholder}
                            />
                          </div>
                        );
                      else if (item.type === "hidden")
                        return (
                          <div class="form-group m-3">
                            <input
                              id={item.id}
                              key={key}
                              placeholder={item.placeholder ? item.placeholder : ""}
                              name={item.name}
                              type="text"
                              value={item.value}
                              class="hidden"
                              checked={item.value}
                              onChange={props.onChange}
                            />
                          </div>
                        );
                      else if (item.type === "checkbox")
                        return (
                          <div class="form-group m-3">
                            <input type="checkbox" id={item.id} key={key} name={item.name} onChange={props.onChange} checked={item.value == "on" ? true : false} />
                            <label for={item.id} class="ml-2">{item.placeholder}</label>
                          </div>
                        );
                      else if (item.type === "textarea")
                        return (
                          <div class="form-group m-3">
                            <textarea
                              onChange={props.onChange}
                              class="form-control
                              block
                              w-full
                              px-3
                              py-1.5
                              text-base
                              font-normal
                              text-gray-700
                              bg-white bg-clip-padding
                              border border-solid border-gray-300
                              rounded
                              transition
                              ease-in-out
                              m-0
                              focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
                              id={item.id}
                              key={key}
                              name={item.name}
                              aria-describedby="emailHelp123"
                              placeholder={item.placeholder}
                              rows={5}
                              cols={5}
                              value={item.value}
                            />
                          </div>
                        );
                      else if (item.type === "select")
                        return (
                          <div class="form-group m-3">
                            <Select
                              isMulti={item.multiselect}
                              placeholder={item.placeholder}
                              value={item.value}
                              name={item.name}
                              onChange={props.onChange}
                              isLoading={selectOptions ? false : true}
                              options={selectOptions ? selectOptions.find((selectitem) => selectitem.id === key)?.options : options}
                              isOptionDisabled={() => item.value.length > item.limit}
                              defaultValue={item.defaultValue}
                            />
                          </div>
                        );
                      else if (item.type === "select-add")
                        return (
                          <div class="form-group m-3">
                            <Select
                              isMulti={item.multiselect}
                              placeholder={item.placeholder}
                              value={item.value}
                              name={item.name}
                              onChange={props.onChange}
                              onKeyDown={(e) => {

                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  let newOption = { value: e.target.value, label: e.target.value };
                                  let newOptions = selectOptions.find((selectitem) => selectitem.id === key)?.options;
                                  if (!newOptions) newOptions = [];
                                  newOptions.push(newOption);
                                  let newSelectOptions = selectOptions.map((selectitem) => {
                                    if (selectitem.id === key) {
                                      selectitem.options = newOptions;
                                    }
                                    return selectitem;
                                  }
                                  );
                                  setSelectOptions(newSelectOptions);
                                }
                              }}
                              isLoading={selectOptions ? false : true}
                              options={selectOptions ? selectOptions.find((selectitem) => selectitem.id === key)?.options : options}
                              isOptionDisabled={() => item.value.length > item.limit}
                              defaultValue={item.defaultValue}
                            />
                          </div>
                        );
                      else if (item.type === "select-fixed")
                        return (
                          <div class="form-group m-3">
                            <Select
                              isMulti={item.multiselect}
                              placeholder={item.placeholder}
                              value={item.value}
                              name={item.name}
                              onChange={props.onChange}
                              isLoading={selectOptions ? false : true}
                              options={item.options.map((option) => { return { value: option, label: option } })}
                              isOptionDisabled={() => item.value.length > item.limit}
                              defaultValue={item.defaultValue}
                            />
                          </div>
                        );
                      else if (item.type === "select-show")
                        return (
                          <div class="form-group m-3">
                            <Select
                              placeholder={item.placeholder}
                              value={item.value}
                              name={item.name}
                              onChange={(e, action) => selectShow(e, action, item.optionIds)}
                              options={item.optionIds.map((optionId) => { return { value: optionId, label: optionId } })}
                              defaultValue={item.defaultValue}
                            />
                          </div>
                        )
                      else if (item.type === "select-datasource")
                        return (
                          <div class="form-group m-3">
                            <Select
                              placeholder={item.placeholder}
                              value={item.value}
                              name={item.name}
                              onChange={(e, action) => selectSource(e, action, item.nextSourceIds)}
                              options={selectOptions ? selectOptions.find((selectitem) => selectitem.id === key)?.options : options}
                              defaultValue={item.defaultValue}
                            />
                          </div>
                        )
                    })}
                  </div>
                  <button
                    type="submit"
                    class="
                      mt-6
                      w-full
                      px-6
                      py-2.5
                      bg-blue-600
                      text-white
                      font-medium
                      text-xs
                      leading-tight
                      uppercase
                      rounded
                      shadow-md
                      hover:bg-blue-700 hover:shadow-lg
                      focus:bg-blue-700 focus:shadow-lg focus:outline-none focus:ring-0
                      active:bg-blue-800 active:shadow-lg
                      transition
                      duration-150
                      ease-in-out"
                  >
                    {props.buttonText}
                  </button>
                </form>
                <p>{props.modalResult}</p>
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={props.onClose}
                className={
                  "focus:outline-none modal-close px-4 p-3 rounded-lg text-white" +
                  (props.disabled
                    ? " bg-gray-100 hover:bg-gray-200"
                    : " bg-gray-400 hover:bg-gray-300")
                }
                disabled={props.disabled}
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Material-UI Dialog */}
      <StyledDialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {props.title}
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {props.modalResult && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 3, 
                borderRadius: '12px',
                '& .MuiAlert-icon': {
                  color: '#667eea',
                }
              }}
            >
              {props.modalResult}
            </Alert>
          )}
          
          <Box component="form" onSubmit={props.onSubmit}>
            {/* Basic Information */}
            {renderFormSection("Información Básica", groups.basic)}
            
            {/* Content */}
            {renderFormSection("Contenido", groups.content)}
            
            {/* Media */}
            {renderFormSection("Multimedia", groups.media)}
            
            {/* Parameters */}
            {renderFormSection("Parámetros", groups.parameters)}
            
            {/* Buttons */}
            {renderFormSection("Botones", groups.buttons)}
            
            {/* Advanced */}
            {renderFormSection("Configuración Avanzada", groups.advanced)}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <StyledButton onClick={handleClose} variant="outlined">
            Cancelar
          </StyledButton>
          <StyledButton
            type="submit"
            variant="contained"
            onClick={props.onSubmit}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            {props.buttonText}
          </StyledButton>
        </DialogActions>
      </StyledDialog>
    </>
  );
}
